import i18next from 'i18next'
import {injectable} from 'inversify'
import {action, computed, makeObservable, observable, reaction, runInAction} from 'mobx'
import {message} from 'antd'
import moment from 'moment/moment'
import {ChartOptions} from 'chart.js'
import {
    AuthStoreInterface,
    generateChartTitleForRange,
    generateInterval,
    getSettlementsTransactionsSummary,
    SettlementsSummaryResponse
} from '@/stores'
import {
    TRangePickerContainerStore,
    TSelectContainerStore,
    TTransactionsCardWithBarChartContainerStore
} from '@/components'
import {LoadingState} from '@/types'
import {Currencies, SERVER_DATE_FORMAT, TITLE_DAY_MONTH_FORMAT} from '@/constants'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {barChartConfig} from '@/services/chart-config'
import {emptyChartDataGenerator} from '@/stores/reports/services/mocks'
@injectable()
export class SettlementsTransactionsCardWithBarChartContainerStore
    implements TTransactionsCardWithBarChartContainerStore
{
    authStore: AuthStoreInterface
    rangePickerStore: TRangePickerContainerStore
    currencySelectStore: TSelectContainerStore
    constructor(
        authStore: AuthStoreInterface,
        rangePickerStore: TRangePickerContainerStore,
        currencySelectStore: TSelectContainerStore
    ) {
        makeObservable(this, {
            dataLoadingState: observable,
            currency: computed,
            data: computed,
            dateRange: computed,
            interval: computed,
            _data: observable,
            setData: action.bound
        })

        reaction(
            () => ({
                from: this.rangePickerStore.startDate,
                to: this.rangePickerStore.endDate,
                currency: this.currencySelectStore.value
            }),
            ({from, to, currency}) => {
                if (from && to && currency && this.authStore.isAuthenticated) {
                    this.loadData()
                }
            }
        )

        this.authStore = authStore
        this.rangePickerStore = rangePickerStore
        this.currencySelectStore = currencySelectStore
        this.currencySelectStore.setValue(DEFAULT_CURRENCY)
        this.loadData()
    }

    dataLoadingState = LoadingState.IDLE
    selectedStatus = undefined
    statuses: undefined
    _data = {amount: undefined}
    selectedDataType = this.dataTypes[0]
    private _abortController: AbortController | null = null

    get data() {
        if (
            this.dataLoadingState !== LoadingState.LOADING &&
            this.dataLoadingState !== LoadingState.FAILED &&
            this.dataLoadingState !== LoadingState.DONE
        ) {
            this.loadData()
        }

        if (!this._data || !this._data.amount) {
            const options = barChartConfig.barOptions({
                title: generateChartTitleForRange({
                    from: this.rangePickerStore.startDate,
                    to: this.rangePickerStore.endDate
                }),
                showTooltip: true
            }) as ChartOptions

            return {
                data: emptyChartDataGenerator(this.dateRange, generateInterval(this.interval)),
                options
            }
        } else
            return {
                data: this._data.amount,
                options: barChartConfig.barCustomTooltipOptions(this._data.amount?.datasets.length)
            }
    }

    get dataTypes() {
        return [
            {
                key: 'amount',
                title: i18next.t('SettlementsTransactionsCardWithBarChartContianerStore.amount')
            }
        ]
    }

    get currency() {
        return this.currencySelectStore.value as Currencies
    }

    get dateRange() {
        return {
            from: this.rangePickerStore.startDate,
            to: this.rangePickerStore.endDate
        }
    }

    get interval() {
        return {
            from: this.rangePickerStore.startDate,
            to: this.rangePickerStore.endDate
        }
    }

    setData = (transactions: SettlementsSummaryResponse[]) => {
        const {from, to} = this.dateRange
        const labels: string[] = []
        const dataPoints: {x: string; y: number | null; full: SettlementsSummaryResponse | null}[] =
            []

        // Create a map of transactions by date for quick lookup
        const transactionMap = new Map<string, SettlementsSummaryResponse>()
        transactions?.forEach((transaction) => {
            const dateKey = moment(transaction.settlementDate).format(TITLE_DAY_MONTH_FORMAT)
            transactionMap.set(dateKey, transaction)
        })

        // Generate all dates in the range
        const diff = moment(to).diff(from, 'days') + 1
        for (let i = 0; i < diff; i++) {
            const currentDate = moment(from).add(i, 'days')
            const dateLabel = currentDate.format(TITLE_DAY_MONTH_FORMAT)
            labels.push(dateLabel)

            const transaction = transactionMap.get(dateLabel)
            if (transaction) {
                dataPoints.push({
                    x: dateLabel,
                    y: transaction.settlementAmount <= 0 ? null : transaction.settlementAmount,
                    full: transaction
                })
            } else {
                dataPoints.push({
                    x: dateLabel,
                    y: null,
                    full: null
                })
            }
        }

        // Get currency from first transaction or use default
        const currency = transactions?.[0]?.currency

        this._data = {
            amount: {
                labels,
                datasets: [
                    {
                        currency,
                        data: dataPoints
                    }
                ]
            }
        }
    }

    setSelectedStatus = (status: string) => {
        this.selectedStatus = status
    }

    setSelectedDataType = (dataTypeKey: string) => {
        this.selectedDataType = this.dataTypes.find((item) => (item.key = dataTypeKey))
    }

    async loadData() {
        this._abortController?.abort()
        const abortController = new AbortController()
        this._abortController = abortController

        runInAction(() => {
            this.dataLoadingState = LoadingState.LOADING
        })

        const request = {
            from: this.rangePickerStore.startDate.format(SERVER_DATE_FORMAT),
            to: this.rangePickerStore.endDate.format(SERVER_DATE_FORMAT),
            currency: this.currencySelectStore.value
        }

        try {
            const {result, error} = await getSettlementsTransactionsSummary(
                request,
                abortController.signal
            )
            if (error) throw new Error(error.message)

            if (this._abortController !== abortController) {
                return
            }

            runInAction(() => (this.dataLoadingState = LoadingState.DONE))

            this.setData(result)
        } catch (error) {
            if (error?.name === 'AbortError') return

            if (this._abortController !== abortController) {
                return
            }

            message.error(error.message)
        }
    }
}
